/**
 * WalletWithdraw — Phase-2 multi-currency rewrite.
 *
 * Currency tabs across the top: USDT (NOWPayments TRC20, active), BDT/INR/PKR
 * disabled with "Coming soon" copy. `?currency=CODE` in the URL pre-selects
 * the tab. USDT flow POSTs `/wallet/withdraw/nowpayments {amount_usdt, address}`.
 *
 * Balances read natively from `user.balances_real[currency]` — no USD
 * conversion anywhere. Withdrawal history lists mixed-currency rows and
 * formats each row using the row's own currency (falling back to USDT for
 * legacy rows written before the money-model change).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Link, useSearchParams } from "react-router-dom";
import {
  AlertTriangle, ArrowRight, CreditCard, Landmark, Lock,
} from "lucide-react";

import api from "../lib/api";
import { extractError } from "../lib/formatError";
import { useAuth } from "../context/AuthContext";
import Button from "../components/ui-r1/Button";
import Input from "../components/ui-r1/Input";
import { Card, CardBody } from "../components/ui-r1/Card";
import { Badge } from "../components/ui-r1/Badge";
import {
  CURRENCIES, WITHDRAW_ENABLED, formatAmount, getBalance,
} from "../lib/currency";
import { isActiveStatus, progressPct, formatWagerLine } from "../lib/bonusHelpers";

// Phase-1 min for USDT withdrawal — mirrors backend `MIN_WITHDRAW_USD` (10).
const MIN_USDT = 10;
const USDT_CHIPS = [10, 50, 100, 500];

export default function WalletWithdraw() {
  const { user, refresh } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // Currency tab. Priority: ?currency=CODE > default USDT (only enabled rail).
  const initialCurrency = useMemo(() => {
    const q = searchParams.get("currency");
    if (q && CURRENCIES.some((c) => c.code === q)) return q;
    return "USDT";
  }, [searchParams]);
  const [currency, setCurrency] = useState(initialCurrency);

  const [kyc, setKyc] = useState(null);
  const [items, setItems] = useState([]);
  const [refreshTick, setRefreshTick] = useState(0);
  // Non-terminal (pending/active) claims per currency drive the wager-lock
  // on this sub-page. Keyed by currency → the actual claim doc so we can
  // render progress + a link back to /promotions to abandon it.
  const [claims, setClaims] = useState([]);

  // USDT form state
  const [amount, setAmount] = useState(50);
  const [address, setAddress] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/kyc/status").then(({ data }) => setKyc(data)).catch(() => {});
    api.get("/wallet/withdrawals").then(({ data }) => setItems(data.items || [])).catch(() => {});
    api.get("/bonuses/mine").then(({ data }) => setClaims(data?.items || [])).catch(() => setClaims([]));
  }, [refreshTick]);

  // Map: currency → non-terminal claim (there's at most one per currency
  // in the money model, so we don't collide). Any bonus type qualifies —
  // if the caller has a wagering bucket for `currency`, they can't pull
  // real balance out until they clear or abandon it.
  const wagerLockClaim = useMemo(() => {
    const map = {};
    for (const c of (claims || [])) {
      if (isActiveStatus(c.status) && c.currency && !map[c.currency]) {
        map[c.currency] = c;
      }
    }
    return map;
  }, [claims]);
  const activeWagerClaim = wagerLockClaim[currency] || null;

  const selectCurrency = useCallback((code) => {
    setCurrency(code);
    const next = new URLSearchParams(searchParams);
    next.set("currency", code);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const kycOk = kyc?.kyc_status === "approved";
  const emailVerifiedOk = !user?.email || user?.email_verified;
  const usdtBalance = getBalance(user, "real", "USDT");
  const activeBalance = getBalance(user, "real", currency);

  const canSubmitUsdt =
    kycOk &&
    emailVerifiedOk &&
    Number(amount) >= MIN_USDT &&
    Number(amount) <= usdtBalance &&
    address.length >= 20 &&
    !busy;

  const submitUsdt = async (e) => {
    e.preventDefault();
    setError(null); setBusy(true);
    try {
      await api.post("/wallet/withdraw/nowpayments", {
        amount_usdt: Number(amount),
        address: address.trim(),
      });
      toast.success("Withdrawal submitted for review");
      setAddress("");
      await refresh();
      setRefreshTick((t) => t + 1);
    } catch (e2) {
      setError(extractError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="wallet-withdraw">
      <div className="mb-6">
        <div className="text-[10px] tracking-[0.4em] uppercase text-pink mb-2">Cash out</div>
        <h2 className="font-display italic text-3xl text-ink">Withdraw</h2>
        <p className="text-sm text-ink-2 mt-2 max-w-xl">
          USDT (TRC20) withdrawals are reviewed within 24 hours and paid manually to your wallet.
          BDT, INR and PKR rails are on the roadmap.
        </p>
      </div>

      {/* Currency tabs */}
      <div
        className="inline-flex bg-base border border-line rounded-xl p-1 mb-5 flex-wrap gap-1"
        role="tablist"
        data-testid="withdraw-currency-tabs"
      >
        {CURRENCIES.map((c) => {
          const enabled = WITHDRAW_ENABLED[c.code];
          const active = currency === c.code;
          return (
            <button
              key={c.code}
              type="button"
              onClick={() => selectCurrency(c.code)}
              className={
                "flex items-center gap-2 px-4 py-2 rounded-lg text-xs tracking-[0.15em] uppercase transition " +
                (active
                  ? "bg-elevated text-pink border border-pink/40 shadow-glow-pink"
                  : enabled ? "text-ink-2 hover:text-ink" : "text-ink-3")
              }
              data-testid={`withdraw-tab-${c.code}`}
              aria-selected={active}
            >
              {c.code === "USDT" && <CreditCard className="w-4 h-4" />}
              {c.code !== "USDT" && <Landmark className="w-4 h-4" />}
              <span className="font-mono">{c.symbol}</span> {c.code}
              {!enabled && <Badge variant="muted" className="ml-1 text-[9px]">Soon</Badge>}
            </button>
          );
        })}
      </div>

      {/* Wager-lock card — takes priority over both the USDT form and the
          "coming soon" copy. Shown whenever the selected currency has a
          non-terminal bonus claim (pending or active). Applies uniformly
          to USDT (which has real withdraw rails) AND fiat (which don't). */}
      {activeWagerClaim && (
        <Card
          className="max-w-md border-gold/60"
          data-testid={`withdraw-wager-lock-${currency}`}
        >
          <CardBody>
            <div className="flex items-center gap-2 mb-3">
              <Lock className="w-4 h-4 text-gold" />
              <Badge variant="gold">Wagering active</Badge>
            </div>
            <div className="font-display italic text-2xl text-ink mb-2">
              Complete or abandon your wagering first
            </div>
            <p className="text-sm text-ink-2 mb-4">
              You&apos;re currently wagering <span className="font-mono text-ink">{activeWagerClaim.bonus_name}</span>{" "}
              in {currency}. Withdrawals for {currency} unlock once wagering hits 100%,
              or you can abandon the bonus (real balance is untouched).
            </p>
            <div className="rounded-lg border border-line bg-base/60 px-3 py-2 mb-4">
              <div className="flex items-center justify-between text-[11px] mb-1.5">
                <div className="tracking-widest uppercase text-ink-2">Progress</div>
                <div className="text-gold font-mono" data-testid={`withdraw-wager-progress-${currency}`}>
                  {formatWagerLine(activeWagerClaim)} · {progressPct(activeWagerClaim)}%
                </div>
              </div>
              <div className="h-1.5 bg-highest rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-pink to-gold transition-[width] duration-500"
                  style={{ width: `${progressPct(activeWagerClaim)}%` }}
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Link
                to="/promotions"
                className="text-[10px] tracking-widest uppercase text-pink border border-pink/40 hover:bg-pink/10 rounded-md px-3 py-2 transition"
                data-testid="withdraw-wager-manage-link"
              >
                Manage in Promotions →
              </Link>
            </div>
          </CardBody>
        </Card>
      )}

      {/* KYC + email gates (only relevant for USDT which is the sole enabled rail) */}
      {!activeWagerClaim && currency === "USDT" && !kycOk && (
        <Card className="mb-6 border-danger/60" data-testid="kyc-required-banner">
          <CardBody className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-danger mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="text-danger text-[11px] tracking-[0.25em] uppercase mb-1">KYC required</div>
              <div className="text-sm text-ink">
                Complete identity verification before you can withdraw.
              </div>
              <div className="text-xs text-ink-2 mt-1">
                Current status:{" "}
                <span className="uppercase text-ink font-mono">{kyc?.kyc_status || "none"}</span>
              </div>
              <Link
                to="/wallet/kyc"
                className="mt-3 inline-flex items-center gap-1 px-4 py-2 rounded-lg bg-brand-gradient text-white text-[11px] tracking-[0.25em] uppercase hover:brightness-110 shadow-glow-pink transition"
                data-testid="link-start-kyc"
              >
                Start verification <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          </CardBody>
        </Card>
      )}

      {!activeWagerClaim && currency === "USDT" && user?.email && !user?.email_verified && (
        <Card className="mb-6 border-gold/60" data-testid="email-verify-required">
          <CardBody className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-gold mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="text-gold text-[11px] tracking-[0.25em] uppercase mb-1">
                Verify your email
              </div>
              <div className="text-sm text-ink">
                We sent a verification link to{" "}
                <span className="font-mono text-ink-2">{user.email}</span>. Confirm it before your
                next withdrawal.
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {!activeWagerClaim && currency === "USDT" && (
        <Card
          className={
            "max-w-md " + (!kycOk || !emailVerifiedOk ? "opacity-60 pointer-events-none" : "")
          }
          data-testid="withdraw-form"
        >
          <CardBody>
            <form onSubmit={submitUsdt}>
              <div className="text-[10px] tracking-[0.25em] uppercase text-ink-2 mb-2">
                Available USDT
              </div>
              <div
                className="font-display italic text-4xl gold-text mb-6"
                data-testid="withdraw-available"
              >
                {formatAmount(usdtBalance, "USDT")}
              </div>

              <div className="mb-5">
                <div className="text-[10px] tracking-[0.25em] uppercase text-ink-2 mb-2">
                  Amount (USDT · min {MIN_USDT})
                </div>
                <div className="grid grid-cols-4 gap-2 mb-3">
                  {USDT_CHIPS.map((c) => (
                    <button
                      type="button"
                      key={c}
                      onClick={() => setAmount(c)}
                      className={
                        "py-2 text-xs font-mono border rounded-lg transition " +
                        (Number(amount) === c
                          ? "border-pink text-pink bg-pink/10"
                          : "border-line text-ink-2 hover:text-ink hover:border-ink-2")
                      }
                      data-testid={`withdraw-chip-${c}`}
                    >
                      {formatAmount(c, "USDT")}
                    </button>
                  ))}
                </div>
                <Input
                  type="number"
                  min={MIN_USDT}
                  max="100000"
                  step="any"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  data-testid="withdraw-amount-input"
                  inputClassName="font-mono"
                />
              </div>

              <Input
                label="TRC20 wallet address"
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="T…"
                data-testid="withdraw-address-input"
                inputClassName="font-mono text-xs"
                hint="Double-check — crypto transfers are irreversible."
                className="mb-4"
              />

              {error && (
                <div
                  className="text-xs text-danger border border-danger/60 bg-danger/10 rounded-lg px-3 py-2 mb-3"
                  data-testid="withdraw-error"
                >
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={!canSubmitUsdt}
                loading={busy}
                variant="primary"
                size="lg"
                className="w-full"
                data-testid="withdraw-submit-btn"
              >
                {busy
                  ? "Submitting…"
                  : `Withdraw ${formatAmount(Number(amount) || 0, "USDT")}`}
              </Button>
            </form>
          </CardBody>
        </Card>
      )}

      {!activeWagerClaim && currency !== "USDT" && (
        <Card className="max-w-md" data-testid={`withdraw-coming-soon-${currency}`}>
          <CardBody>
            <Badge variant="muted" className="mb-3">Coming soon</Badge>
            <div className="font-display italic text-2xl text-ink mb-2">
              {currency} withdrawals aren't live yet
            </div>
            <p className="text-sm text-ink-2 mb-4">
              Your {currency} balance is{" "}
              <span className="font-mono text-ink">
                {formatAmount(activeBalance, currency)}
              </span>
              . Withdrawal rails for {currency} are on the roadmap — for now, convert in-game to
              USDT or reach out to support.
            </p>
            <div className="text-[10px] tracking-widest uppercase text-ink-3">
              Switch to USDT above to cash out today ↑
            </div>
          </CardBody>
        </Card>
      )}

      {/* History */}
      <div className="mt-10" data-testid="withdrawal-history">
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <div className="text-[10px] tracking-[0.35em] uppercase text-pink">Ledger</div>
            <h3 className="font-display italic text-2xl text-ink">Recent withdrawals</h3>
          </div>
          <Link
            to="/history"
            className="text-[10px] tracking-widest uppercase text-ink-2 hover:text-pink"
          >
            Full history →
          </Link>
        </div>
        {items.length === 0 ? (
          <Card>
            <CardBody className="text-sm text-ink-2 italic text-center py-8">
              No withdrawals yet.
            </CardBody>
          </Card>
        ) : (
          <Card className="overflow-hidden">
            <div className="divide-y divide-line">
              {items.slice(0, 8).map((w) => {
                // Legacy rows pre-multi-currency may not carry `currency` on
                // the /withdrawals response — default to USDT for display.
                const cur = (w.currency && CURRENCIES.some((c) => c.code === w.currency))
                  ? w.currency
                  : "USDT";
                return (
                  <div
                    key={w.id}
                    className="grid grid-cols-[1fr_auto_auto] gap-4 px-5 py-4 items-center"
                    data-testid={`withdrawal-${w.id}`}
                  >
                    <div>
                      <div className="font-mono text-sm text-ink">
                        {formatAmount(w.amount_usd, cur)} · {w.crypto_currency}
                      </div>
                      <div className="text-[10px] text-ink-3 font-mono break-all">
                        {w.crypto_address.slice(0, 12)}…{w.crypto_address.slice(-8)}
                      </div>
                      {w.reject_reason && (
                        <div className="text-[10px] text-danger mt-1">
                          Rejected: {w.reject_reason}
                        </div>
                      )}
                      {w.tx_hash && (
                        <div className="text-[10px] text-gold mt-1 font-mono break-all">
                          tx: {w.tx_hash}
                        </div>
                      )}
                    </div>
                    <StatusBadge status={w.status} />
                    <div className="text-[10px] text-ink-3">
                      {new Date(w.created_at).toLocaleDateString()}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: { variant: "muted", label: "Pending" },
    paid: { variant: "success", label: "Paid" },
    rejected: { variant: "danger", label: "Rejected" },
    approved: { variant: "gold", label: "Approved" },
  };
  const m = map[status] || { variant: "muted", label: status };
  return (
    <Badge variant={m.variant} data-testid={`status-${status}`}>
      {m.label}
    </Badge>
  );
}
